import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getNonLoyaltyProfile, searchRenterByMembership, setDriveProfileState } from 'actions/profile';
import {
  getReservationProfileLoyaltyNumber,
  reservationDriverInfoSelector,
  uiReservationDriverProfileSelector,
} from 'selectors/reservationSelectors';
import { onlineCheckInProfileSelector } from 'selectors/checkinSelectors';
import useActions from 'hooks/useActions';

/**
 * usePostResDriverProfile
 * Custom hook to handle Driver Profile fetch for Post Reservation flows (OCI & STC).
 * If app state contains OCI Profile info this set of data will be returned. Otherwise, the Driver Profile
 * will be fetched by Loyalty Number, if available, or Driver Profile Individual Identifier. The requested
 * data set will be stored in Driver Profile State (`ui`) and returned.
 *
 * @return {object}
 * @property {object} license_profile
 * @property {object} license_profile.country_code
 * @property {object} license_profile.country_name
 * @property {object} license_profile.country_subdivision_code
 * @property {object} license_profile.license_number
 * @property {object} license_profile.license_expiration_date
 * @property {object} license_profile.birth_date
 * @property {object} address_profile
 * @property {array} address_profile.street_addresses
 * @property {object} address_profile.country_name
 * @property {object} address_profile.country_code
 * @property {object} address_profile.country_subdivision_code
 * @property {object} address_profile.city
 * @property {object} address_profile.postal
 * @property {object} contact_profile
 * @property {array} contact_profile.phones
 */
const usePostResDriverProfile = () => {
  const handleGetNonLoyaltyProfile = useActions(getNonLoyaltyProfile);
  const handleSearchRenterByMembership = useActions(searchRenterByMembership);
  const handleSetDriverProfileState = useActions(setDriveProfileState);

  const ociDriverProfile = useSelector(onlineCheckInProfileSelector);
  const uiDriverProfile = useSelector(uiReservationDriverProfileSelector);
  const reservationLoyaltyNumber = useSelector(getReservationProfileLoyaltyNumber);
  const reservationDriverInfo = useSelector(reservationDriverInfoSelector);
  const { last_name, individual_indentifier } = reservationDriverInfo || {};

  // If Driver's License Country Code is available, OCI Profile is available to be used
  const haveOCIDriverProfile = !!ociDriverProfile.license_profile.country_code;
  const prevHaveOCIDriverProfileRef = useRef(haveOCIDriverProfile);

  const setProfile = (args) => handleSetDriverProfileState({ args });

  useEffect(() => {
    // Session losing OCI profile means that GMA commited the reservation to GBO and cleared up
    // the GMA session for OCI, so FE dont need to call the get driver info request
    const lostOCIProfile = !haveOCIDriverProfile && !!prevHaveOCIDriverProfileRef.current;

    // If OCI Driver Profile is not available, fetch for Driver's info by Loyalty Number (Loyalty Profile)
    // or Individual Identifier (NL Profile) in Reservation
    if (!haveOCIDriverProfile && !lostOCIProfile) {
      if (reservationLoyaltyNumber) {
        handleSearchRenterByMembership({ last_name, loyalty_number: reservationLoyaltyNumber }).then(setProfile);
      } else if (individual_indentifier) {
        handleGetNonLoyaltyProfile(individual_indentifier).then(setProfile);
      }
    }

    prevHaveOCIDriverProfileRef.current = haveOCIDriverProfile;
  }, [ociDriverProfile, individual_indentifier, reservationLoyaltyNumber]);

  return haveOCIDriverProfile ? ociDriverProfile : uiDriverProfile;
};

export default usePostResDriverProfile;
